import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useGetDialerNumbersQuery,
  useGetHomeQuery,
  useRestDialerNumberMutation,
  useRestoreDialerNumberMutation,
  useSetDialerNumberDailyCapMutation,
  useSetDialerReturnCallDaysMutation,
} from "@/store/rest_api";
import type { DialerNumberHealth } from "@/model/interface/PhoneNumber";
import { DialerNumberStatus } from "@/enum/DialerNumberStatus";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";
import { apiErrorMessage } from "@/util/apiError";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function statusOf(number: DialerNumberHealth, dailyCap: number): { label: string; pillClass: string } {
  if (number.dialerStatus === DialerNumberStatus.RESTED) {
    return { label: "Resting", pillClass: "status-pill-warning" };
  }
  if (number.looksFlagged) {
    return { label: "Looks flagged", pillClass: "status-pill-destructive" };
  }
  if (number.callsToday >= dailyCap) {
    return { label: "At today's limit", pillClass: "status-pill-neutral" };
  }
  return { label: "In rotation", pillClass: "status-pill-success" };
}

export default function DialerNumbersTab() {
  const { data: user } = useGetHomeQuery();
  const { data: overview, isLoading } = useGetDialerNumbersQuery();
  const [setDailyCap, { isLoading: isSavingCap }] = useSetDialerNumberDailyCapMutation();
  const [restNumber, { isLoading: isResting }] = useRestDialerNumberMutation();
  const [restoreNumber, { isLoading: isRestoring }] = useRestoreDialerNumberMutation();
  const [setReturnCallDays, { isLoading: isSavingReturnCallDays }] = useSetDialerReturnCallDaysMutation();
  const [capDraft, setCapDraft] = useState("");
  const [returnCallDaysDraft, setReturnCallDaysDraft] = useState("");
  const membership = user?.activeMembership;
  const isAdmin =
    membership?.status === MembershipStatus.ACTIVE &&
    (membership.role === MembershipRole.OWNER || membership.role === MembershipRole.ADMIN);

  const handleSaveCap = async () => {
    try {
      await setDailyCap(Number(capDraft)).unwrap();
      toast.success("Daily limit saved");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not save the daily limit"));
    }
  };

  const handleSaveReturnCallDays = async () => {
    try {
      await setReturnCallDays(Number(returnCallDaysDraft)).unwrap();
      toast.success("Return call routing saved");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not save return call routing"));
    }
  };

  const handleToggleRest = async (number: DialerNumberHealth) => {
    try {
      if (number.dialerStatus === DialerNumberStatus.RESTED) {
        await restoreNumber(number.id).unwrap();
        toast.success("Number back in rotation");
      } else {
        await restNumber(number.id).unwrap();
        toast.success("Number resting. The dialer won't use it until you restore it.");
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not update the number"));
    }
  };

  useEffect(() => {
    if (overview) {
      setCapDraft(String(overview.dailyCap));
    }
  }, [overview?.dailyCap]);

  useEffect(() => {
    if (overview) {
      setReturnCallDaysDraft(String(overview.returnCallDays));
    }
  }, [overview?.returnCallDays]);

  if (isLoading || !overview) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Daily Call Limit</CardTitle>
          <CardDescription>
            Each dialer number places at most this many calls a day. Numbers at the limit leave rotation until
            tomorrow. The platform allows up to {overview.platformDailyCap}.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={overview.platformDailyCap}
            value={capDraft}
            onChange={(event) => setCapDraft(event.target.value)}
            disabled={!isAdmin}
            className="w-32"
          />
          <span className="text-sm text-muted-foreground">calls per number per day</span>
          {isAdmin && (
            <Button
              className="ml-auto"
              onClick={() => void handleSaveCap()}
              disabled={isSavingCap || capDraft === String(overview.dailyCap)}
            >
              {isSavingCap && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Return Calls</CardTitle>
          <CardDescription>
            When a lead calls back after talking to an agent through the dialer, their call rings that agent first,
            then continues with the number's call flow. Agents in a dialer session see it as Take call. Set 0 to
            turn this off.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            max={overview.maxReturnCallDays}
            value={returnCallDaysDraft}
            onChange={(event) => setReturnCallDaysDraft(event.target.value)}
            disabled={!isAdmin}
            className="w-32"
          />
          <span className="text-sm text-muted-foreground">days after the dialer conversation</span>
          {isAdmin && (
            <Button
              className="ml-auto"
              onClick={() => void handleSaveReturnCallDays()}
              disabled={isSavingReturnCallDays || returnCallDaysDraft === String(overview.returnCallDays)}
            >
              {isSavingReturnCallDays && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Dialer Numbers</CardTitle>
          <CardDescription>
            The last 30 days. A low answer rate or many calls under 8 seconds can mean carriers label the number as
            spam. Rest it for a few days. Every dialer number needs a call flow that reaches your business and takes
            Do Not Call requests.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead className="text-right">Today</TableHead>
                <TableHead className="text-right">Dials</TableHead>
                <TableHead className="text-right">Answer rate</TableHead>
                <TableHead className="text-right">Under 8 s</TableHead>
                <TableHead className="text-right">Abandoned</TableHead>
                <TableHead>Call flow</TableHead>
                <TableHead>Status</TableHead>
                {isAdmin && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {overview.numbers.map((number) => {
                const status = statusOf(number, overview.dailyCap);
                return (
                  <TableRow key={number.id}>
                    <TableCell>
                      <div className="font-medium">{number.number}</div>
                      {number.alias && <div className="text-xs text-muted-foreground">{number.alias}</div>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {number.callsToday} / {overview.dailyCap}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{number.dials}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {number.dials > 0 ? `${Math.round(number.answerRate * 100)}%` : "None yet"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{number.shortCalls}</TableCell>
                    <TableCell className="text-right tabular-nums">{number.abandons}</TableCell>
                    <TableCell>
                      {number.hasCallFlow ? (
                        <span className="text-sm">Set</span>
                      ) : (
                        <span className="text-sm text-destructive">Missing</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`status-pill ${status.pillClass} px-2.5 py-0.5 text-xs`}>{status.label}</span>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isResting || isRestoring}
                          onClick={() => void handleToggleRest(number)}
                        >
                          {number.dialerStatus === DialerNumberStatus.RESTED ? "Restore" : "Rest"}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {overview.numbers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 9 : 8} className="py-6 text-center text-sm text-muted-foreground">
                    No active phone numbers yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
